define('viewModels/dashboards/EvaluatorDashboard',[
    'lib/lodash4',
    'lib/knockout',
    'lib/bluebird',
    'moment',
    'viewModels/ViewModel',
    'viewModels/privileged/qm/policies/editors/DateRangeEditorView',
    'viewModels/dashboards/qualityAdminDashboard/AgentActivityWidget',
    'util/errorCallbackFactory',
    'services/authService',
    'services/qm/qualityAuth',
    'services/qualityManagementService',
    'services/featureToggleService',
    'services/loggingService'
], function (
        _,
        ko,
        Promise,
        moment,
        ViewModel,
        DateRangeEditorView,
        AgentActivityWidget,
        errorCallbackFactory,
        authService,
        qualityAuth,
        qmService,
        featureToggleService,
        loggingService
    ) {
    'use strict';

    var PENDING_EVALUATION_OPTIONS = {
        evaluationState: [qmService.PENDING, qmService.INPROGRESS]
    };

    var COMPLETED_EVALUATION_OPTIONS = {
        evaluationState: [qmService.FINISHED]
    };

    var PENDING_GRID_COLUMNS = [
        {
            id: 'assignedDate',
            label: 'evaluatorDashboard.pendingEvaluations.assignedDateTime'
        },
        {
            id: 'agent',
            label: 'evaluatorDashboard.pendingEvaluations.agent'
        },
        {
            id: 'form',
            label: 'evaluatorDashboard.pendingEvaluations.formName'
        },
        {
            id: 'status',
            label: 'evaluatorDashboard.pendingEvaluations.status'
        },
        {
            id: 'calibration',
            label: "evaluatorDashboard.pendingEvaluations.isForCalibration"
        }
    ];

    var COMPLETED_GRID_COLUMNS = [
        {
            id: 'assignedDate',
            label: 'evaluatorDashboard.completedEvaluations.assignedDateTime'
        },
        {
            id: 'releaseDate',
            label: 'evaluatorDashboard.completedEvaluations.releaseDateTime'
        },
        {
            id: 'agent',
            label: 'evaluatorDashboard.completedEvaluations.agent'
        },
        {
            id: 'form',
            label: 'evaluatorDashboard.completedEvaluations.formName'
        },
        {
            id: 'score',
            label: "evaluatorDashboard.completedEvaluations.score"
        },
        {
            id: 'calibration',
            label: "evaluatorDashboard.completedEvaluations.isForCalibration"
        }
    ];

    var TIME_REMAINING_TO_EVALUATE_COLUMN = {
        id: 'timeRemainingToEvaluate',
        label: 'evaluatorDashboard.pendingEvaluations.timeRemainingToEvaluate.title'
    };

    var DEFAULT_GRID_OPTIONS = {
        pageCount: 1,
        pageNumber: 1,
        pageSize: 25
    };

    var oneMinuteMs = 60 * 1000;
    var oneHourMs = 60 * 60 * 1000;
    var oneDayMs = 24 * 60 * 60 * 1000;

    //this function is only here because Math.round() by itself sometimes does not produce correct results
    //i.e if you put in Math.round(1.005 * 100) / 100, you would 1.00, instead of 1.01
    var roundUpDecimals = function(num){
        return +(Math.round(num + "e+2") + "e-2");
    };

    function makeEvaluationRow(evaluation) {
        try {
            var rowData = {
                id: evaluation.id,

                agent: evaluation.agent,
                assignedDate: moment(evaluation.assignedDate).toDate(),
                calibration: evaluation.calibration,
                conversation: evaluation.conversation,
                evaluationForm: evaluation.evaluationForm,
                releaseDate: evaluation.releaseDate ? moment(evaluation.releaseDate).toDate() : null,
                createdDate: evaluation.createdDate ? moment(evaluation.createdDate).toDate() : null,
                timeRemainingToEvaluate: ko.observable(),
                score: ko.observable(),
                status: evaluation.status.toLowerCase()
            };

            //Add the score post-row creation
            //TODO: remove this when the query endpoint contains answer details
            if (isCompleted(evaluation)) {
                qmService.getAssignedEvaluation(evaluation.conversation.id, evaluation.id)
                    .then(function (result) {
                        rowData.score(roundUpDecimals(result.answers.totalScore));
                    }).catch(function () {
                        rowData.score(ko.i18n.renderString('evaluatorDashboard.score.unavailable'));
                    });
            }

            return rowData;
        } catch (e) {
            var evaluationId = evaluation ? evaluation.id : "undefined";
            loggingService.error("Could not generate a row for evaluation " + evaluationId + ". " + e);
            return;
        }
    }

    function isCompleted(evaluation) {
        return (evaluation.status.toLowerCase() == qmService.FINISHED.toLowerCase());
    }

    function populateGrid(gridModel, evaluations) {
        gridModel.rows(_.chain(evaluations)
                             .map(makeEvaluationRow)
                             .compact()
                             .valueOf());
    }

    function pickPageData(response) {
        return _.pick(response, [
                'pageCount',
                'pageSize',
                'pageNumber'
            ]);
    }

    function getAssignedEvaluations(options) {
        options.evaluatorUserId = authService.principalId();
        options.expand = ['agent'];
        return qmService.getAssignedEvaluations(options);
    }


    /**
     * fetch data for and populate a dataGrid
     *
     * @param  {object} grid
     * @param  {object} dataOptions
     * @returns {Promise} an evaluation query response
     */
    function refreshGrid(grid, incomingDataOptions) {
        var times = grid.activityDateRange()[0].split("/");
        var dateOptions = {
            startTime: times[0],
            endTime: times[1]
        };
        var options = _.assign({}, dateOptions, grid.dataOptions(), incomingDataOptions);
        return getAssignedEvaluations(options)
            .then(function (queryResponse) {
                var newPageData = ko.toJS(pickPageData(queryResponse));
                var newDataOptions = _.assign({}, grid.dataOptions(), incomingDataOptions, newPageData);
                grid.dataOptions(newDataOptions);
                populateGrid(grid, queryResponse.entities());
            })
             .catch(
                 function (error) {
                    if(error &&
                        error.details &&
                        error.details.response &&
                        error.details.response.code === "quality.query.invalid.user")
                    {
                        errorCallbackFactory.createCallback({namespace: 'evaluatorDashboard.queryEvaluations.invalidUser'})(error);
                    }
                    else {
                        errorCallbackFactory.createCallback({namespace: 'evalatorDashboard.loadEvaluations.error'})(error);
                    }
                 });
    }

    function getFormattedTimeRemainingToEvaluate(timeRemainingToEvaluateMs){
        if(timeRemainingToEvaluateMs > oneDayMs){
            return ko.i18n.renderString({
                'name': 'evaluatorDashboard.pendingEvaluations.timeRemainingToEvaluate.days',
                'data': {
                    "daysRemaining": Math.floor(timeRemainingToEvaluateMs / oneDayMs),
                    "hoursRemaining": Math.floor((timeRemainingToEvaluateMs % oneDayMs) / oneHourMs)
                }
            });
        } else if(timeRemainingToEvaluateMs > 0) {
            // be more granular when less than a day left
            return ko.i18n.renderString({
                'name': 'evaluatorDashboard.pendingEvaluations.timeRemainingToEvaluate.hours',
                'data': {
                    "hoursRemaining": Math.floor(timeRemainingToEvaluateMs / oneHourMs),
                    "minutesRemaining": Math.floor((timeRemainingToEvaluateMs % oneHourMs) / oneMinuteMs)
                }
            });
        } else {
            return ko.i18n.renderString('evaluatorDashboard.pendingEvaluations.timeRemainingToEvaluate.outOfTime');
        }
    }

    /**
     *  Evaluator Dashboard
     */
    var EvaluatorDashboard = ViewModel.extend(function () {

        //
        // Private Variables
        //

        var self = this;
        var startOfMonth = moment().startOf('month').toDate();
        var endOfMonth = moment().endOf('month').toDate();
        var activityDateRange = ko.observableArray([startOfMonth.toISOString() + '/' + endOfMonth.toISOString()]);

        //
        // Public Attributes
        //

        self.dateRangesView = self.subView(DateRangeEditorView, activityDateRange);

        self.isLoadingEvaluations = ko.observable(true);

        self.completedEvalGridOptions = ko.observable(_.assign({}, COMPLETED_EVALUATION_OPTIONS, DEFAULT_GRID_OPTIONS));

        self.completedEvalGrid = {
            columns: COMPLETED_GRID_COLUMNS,
            rows: ko.observableArray([]),
            dataOptions: self.completedEvalGridOptions
        };

        self.pendingEvalGridOptions = ko.observable(_.assign({}, PENDING_EVALUATION_OPTIONS, DEFAULT_GRID_OPTIONS));

        self.pendingEvalGrid = {
            columns: PENDING_GRID_COLUMNS.slice(),
            rows: ko.observableArray([]),
            dataOptions: self.pendingEvalGridOptions
        };

        self.pendingEvalGrid.columns.splice(4, 0, TIME_REMAINING_TO_EVALUATE_COLUMN);

        self.hasCompletedEvaluations = self.computed(function () {
            return self.completedEvalGrid.rows().length > 0;
        });

        self.hasPendingEvaluations = self.computed(function () {
            return self.pendingEvalGrid.rows().length > 0;
        });

        self.agentActivityWidget = self.subView(AgentActivityWidget, activityDateRange, authService.principalId);

        self.canViewEvaluationDashboard = qualityAuth.canEditEvaluation() || qualityAuth.canEditScore();

        // grid bulk property assignment
        var evalGrids = [self.completedEvalGrid, self.pendingEvalGrid];
        _.each(evalGrids, function (grid) {
            var _refreshGrid = refreshGrid.bind(self, grid);
            // the onDataOptionsChange callback will supply the new dataOptions
            // object as a second parameter to refreshGrid
            grid.onDataOptionsChange = _refreshGrid;
            grid.activityDateRange = activityDateRange;
        });

        self._refreshEvaluationGrids = function () {
            self.isLoadingEvaluations(true);

            var promises = _.map(evalGrids, function (grid) {
                return grid.onDataOptionsChange(grid.dataOptions());
            });

            Promise.all(promises)
                .finally(function () {
                    self.updateTimesRemainingToEvaluate();
                    self.isLoadingEvaluations(false);
                });
        };

        self.updateTimesRemainingToEvaluate = function(){
            if(!self.pendingEvalGrid || !self.pendingEvalGrid.rows){
                return;
            }
            _.each(self.pendingEvalGrid.rows(), function(row){
                if(!row.createdDate && !row.assignedDate){
                    return;
                }
                var timeRemainingToEvaluateMs = moment(row.createdDate || row.assignedDate).add(45, 'days').diff(moment());
                row.timeRemainingToEvaluate(getFormattedTimeRemainingToEvaluate(timeRemainingToEvaluateMs));
            });
        };

        // Update each minute the amount of time left to complete each evaluation
        var updateTimesRemainingToEvaluateTimer;
        (function regularlyUpdateTimesRemaining(){
            self.updateTimesRemainingToEvaluate();
            updateTimesRemainingToEvaluateTimer = setTimeout(regularlyUpdateTimesRemaining, oneMinuteMs);
        })();

        // refresh grids when updating activity date range
        self.subscribe(activityDateRange, function () {
            // reset the grids back to their default configurations
            _.each(evalGrids, function (grid) {
                grid.dataOptions(_.assign(grid.dataOptions(), DEFAULT_GRID_OPTIONS));
            });
            self._refreshEvaluationGrids();
        });

        // fetch initial grid data
        self._refreshEvaluationGrids();

        self.onDispose = function(){
            clearTimeout(updateTimesRemainingToEvaluateTimer);
        };
    });

    return EvaluatorDashboard;
});

